import React from "react";
import {
  Box,
  Container,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

export default function PrivacyBCP() {
  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ p: 2 }} maxWidth="lg">
        <Typography textAlign="center" variant="h3" paragraph>
          Best Coast Pairings LLC Privacy Policy ("Privacy Policy")
        </Typography>
        <Typography variant="body1" paragraph>
          Date Privacy Policy Last Modified: February 2025
        </Typography>

        <>
          <Typography variant="h4" paragraph>
            INTRODUCTION
          </Typography>
          <Typography variant="body1" paragraph>
            Best Coast Pairings LLC ("<strong>Company</strong>" or "
            <strong>We</strong>") respects your privacy and are committed to
            protecting your personal data through our compliance with this
            Privacy Policy.
          </Typography>
          <Typography variant="body1" paragraph>
            This Policy will inform you as to how we look after your personal
            data, your privacy rights, how the law protects you, and our
            practices for collecting, using, maintaining, protecting and
            disclosing your personal data when you visit our website
            BestCoastPairings.com (our "<strong>Website</strong>") or use any of
            the Company's services (including, without limitation, any desktop
            or mobile applications, products or other services) (collectively,
            the Website and services are referred to as the "
            <strong>Services</strong>"). As you are using our Services, we want
            to be clear about how we use information and the ways in which you
            can protect your privacy.
          </Typography>
          <Typography variant="body1" paragraph>
            Please read this Policy carefully to understand our policies and
            practices regarding your personal data and how we will treat it. If
            you do not agree with our policies and practices, your choice is not
            to use our Services because we will not be able to perform the
            contract we have or are trying to enter with you (for example, to
            provide you with services).
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using our Services, you agree to this Privacy
            Policy, and if you are under age 16, you have your parent or legal
            guardian's permission to use our Services and your parent/legal
            guardian has agreed to this Privacy Policy.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            1. IMPORTANT INFORMATION AND WHO WE ARE
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>PURPOSE OF THIS PRIVACY NOTICE.</strong> This Privacy Policy
            aims to give you information on how Best Coast Pairings LLC collects
            and processes your personal data (in various formats, including
            digitally, physically, and abstract physical formats) through your
            use of the Services, including any data you may provide through the
            Services when you purchase a subscription or participate in an
            Event.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>CONTROLLER.</strong> For purposes of the European Union's ("
            <strong>EU</strong>") General Data Protection Regulation 2016/679 ("
            <strong>GDPR</strong>"), Best Coast Pairings LLC (the "
            <strong>Company</strong>") is the controller and responsible for
            this Website. In some instances, the Company may act as a processor
            on behalf of another controller, for example, when the Company's
            Services are used to power the ranking system of another company
            (e.g. Games Workshop, Catan Studio, etc.).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>DATA PRIVACY MANAGER.</strong> We have appointed a data
            privacy manager who is responsible for overseeing questions in
            relation to this Policy and the GDPR. If you have any questions
            about this Policy, including any requests to exercise your legal
            rights, please contact the Company's data privacy manager using the
            details set out below.
          </Typography>
          <Typography variant="body1">Subject: Best Coast Pairings Data Policy Question</Typography>
          <Typography variant="body1" paragraph>
            <Link href="mailto:support@bestcoastpairings.com" target="_blank">
              support@bestcoastpairings.com
            </Link>
          </Typography>
          <Typography variant="body1" paragraph>
            Individuals located in the EU have the right to make a complaint at
            any time to the appropriate EU supervisory authority for data
            protection issues. We would, however, appreciate the chance to deal
            with your concerns before you approach the supervisory authority, so
            please contact us in the first instance.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>
              CHANGES TO THE PRIVACY POLICY AND YOUR DUTY TO INFORM US OF
              CHANGES.
            </strong>
            It is our policy to post any changes we make to our privacy policy
            on this page. If we make material changes to how we treat our users'
            personal information, we will notify you through a notice on the
            Website home page, banner within the mobile application, or by
            sending an email to the email address you provided to us. Your
            continued use of this Website following the effective date of such
            update constitutes your acceptance of the revised privacy policy.
          </Typography>
          <Typography variant="body1" paragraph>
            You are responsible for ensuring we have an up-to-date active and
            deliverable e-mail address for you, and for periodically visiting
            our Services and this privacy policy to check for any changes. The
            date the privacy policy was last revised is identified at the top of
            this page. Historic versions are available by emailing{" "}
            <Link href="mailto:support@bestcoastpairings.com" target="_blank">
              support@bestcoastpairings.com
            </Link>{" "}
            and requesting copies.
          </Typography>
          <Typography variant="body1" paragraph>
            It is important that the personal information, and importantly, the
            contact information we hold about you is accurate and current.
            Please keep us informed if your personal data changes during your
            relationship with us.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>THIRD PARTY LINKS.</strong> The Services may include links
            (and advertisements) to third-party websites, plug-ins, and
            applications. Clicking on those links or enabling those connections
            may allow third parties to collect or share data about you. We do
            not control these third-party websites and are not responsible for
            their privacy statements/notices. When you leave our Services
            (including the Website), we encourage you to read the privacy notice
            of every website you visit and application you use.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            2. THE DATA WE COLLECT ABOUT YOU
          </Typography>
          <Typography variant="body1" paragraph>
            "<strong>Personal Data</strong>" (or personal information) means any
            information about an individual from which that person can be
            identified. It does not include data where the person's identity has
            been removed.
          </Typography>
          <Typography variant="body1" paragraph>
            This Policy applies to Personal Data we collect from or about you in
            any format.
          </Typography>
          <Typography variant="body1" paragraph>
            We collect several types of Personal Data from and about users of
            our Services, including the following:
          </Typography>
          <Typography variant="body1" paragraph>
            <ol
              style={{
                display: "block",
                listStyleType: "decimal",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                <strong>Identity Data includes:</strong> first name and last
                name
              </li>
              <li>
                <strong>Contact Data includes:</strong> email address.
              </li>
              <li>
                <strong>Technical Data includes:</strong> internet protocol (IP)
                addresses, your login data, your internet connection, browser
                type and version, time zone setting and location, browser
                plug-in types and versions, operating system and platform, and
                information about the equipment (computer or mobile device) you
                use to access our Services.
              </li>
              <li>
                <strong>Transaction Data</strong> includes details about
                transactions with you and other details of Services you have
                purchased from us.
              </li>
              <li>
                <strong>Profile Data includes:</strong> event scores (including
                by round) and rankings, army lists, and table assignments.
              </li>
              <li>
                <strong>Usage Data includes:</strong> information about how you
                use our Services.
              </li>
              <li>
                <strong>Marketing and Communications Data includes:</strong>{" "}
                your preferences in receiving marketing from us and any third
                parties, and your communication preferences.
              </li>
            </ol>
          </Typography>
          <Typography variant="body1" paragraph>
            We also collect, use, and share "<strong>Aggregated Data</strong>"
            such as statistical or demographic data for any purpose. Aggregated
            Data may be derived from your Personal Data but is not considered
            Personal Data because this data does not directly or indirectly
            reveal your identity. For example, we may aggregate your Usage Data
            to calculate the percentage of users accessing a specific Service.
            However, if we combine or connect Aggregated Data with your
            information so that it can directly or indirectly identify you, we
            will treat the combined data as Personal Data which will be used in
            accordance with this Privacy Policy.
          </Typography>
          <Typography variant="body1" paragraph>
            We also use a third-party payment processor to handle all payment
            processing transaction data, and we, therefore, do not have access
            to any Financial Data, such as details about payments, billing
            addresses, or debit/credit card information.
          </Typography>
          <Typography variant="body1" paragraph>
            We collect Technical Data which includes: internet protocol ("
            <strong>IP</strong>") address, your login data, browser type and
            version, time zone settings and location, browser plug-in types and
            versions, traffic data, logs and other communication data and the
            resources that you access and use on the Services, operating system
            and platform, and other technology on the devices you use to access
            our Services. As you navigate through and interact with our
            Services, we may use automatic data collection technologies to
            collect certain Technical Data about your equipment, browsing
            actions and patterns. We do not use automated technologies to
            collect information about your online activities over time and
            across third-party websites or other online services (behavioral
            tracking).
          </Typography>
          <Typography variant="body1" paragraph>
            We do not maintain or associate the Technical Data we collect with
            your Personal Data. The Technical Data helps us to improve our
            Services and to deliver a better and more personalized service,
            including by enabling us to:
          </Typography>
          <Typography variant="body1" paragraph>
            <ol
              style={{
                display: "block",
                listStyleType: "decimal",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>Estimate our audience size and usage patterns.</li>
              <li>
                Store information about your preferences, allowing us to
                customize our Services according to your individual interests.
              </li>
              <li>Speed up your searches.</li>
              <li>Recognize you when you return to our Services.</li>
            </ol>
          </Typography>
          <Typography variant="body1" paragraph>
            We do not collect any Special Categories of Personal Data about you
            (such as details about your race or ethnicity, religious or
            philosophical beliefs, sex life, sexual orientation, political
            opinions, trade union membership, information about your health, or
            genetic and biometric data). Nor do we collect any information about
            criminal convictions or offenses.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            3. HOW IS YOUR PERSONAL DATA COLLECTED
          </Typography>
          <Typography variant="body1" paragraph>
            We use different methods to collect this information from you and
            about you including:
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>DIRECT INTERACTIONS.</strong> You may give us your Personal
            Data by filling in forms or by corresponding with us by mail, phone,
            email or otherwise. This includes Personal Data you provide when
            you:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                Register and use our Services (such as entering event scores,
                army lists, etc.)
              </li>
              <li>Create an account on our Website or Services;</li>
              <li>Subscribe to our Services;</li>
              <li>Request marketing be sent to you;</li>
              <li>
                or Give us some feedback or report a problem with our Services.
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>AUTOMATED TECHNOLOGIES OR INTERACTIONS.</strong> As you
            interact with our Website or other Services, we may automatically
            collect Technical Data about your equipment, browsing actions and
            patterns as you navigate through the Services. Information collected
            automatically may include usage details, IP addresses and
            information collected through cookies.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>THIRD PARTIES OR PUBLICLY AVAILABLE SOURCES.</strong> We may
            receive Personal Data about you from various third parties and
            public sources as set out below:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                Technical Data from the following parties:
                <ul
                  style={{
                    display: "block",
                    marginBlockStart: "4px",
                    marginBlockEnd: "4px",
                    marginInlineStart: "0px",
                    marginInlineEnd: "0px",
                    paddingInlineStart: "40px",
                  }}
                >
                  <li>
                    Analytics providers (such as, Google, based outside the EU);
                  </li>
                  <li>Advertising networks, based inside or outside the EU;</li>
                  <li>
                    Search information providers, based inside or outside the
                    EU;
                  </li>
                  <li>
                    Contact, Financial or Transaction Data from our third-party
                    payment processor based inside or outside the EU submitted
                    by you detailing the transactions you carry out through our
                    Services and of the fulfillment of your orders;
                  </li>
                  <li>
                    and Identity, Contact and registration Data from our
                    business partners, such as Frontline Gaming, based outside
                    the EU for event registration purposes.
                  </li>
                </ul>
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            The Website does not have the functionality for a forum when users
            submit contributions directly, but you may provide information to be
            published or displayed (hereinafter, "<strong>posted</strong>") on
            public areas of the Services, or transmitted to other users of the
            Services or third parties, such as event scores, army lists
            (collectively, "<strong>User Contributions</strong>"). User
            Contributions will be posted on and transmitted to others at your
            own risk. Please be aware that no security measures are perfect or
            impenetrable. Additionally, we cannot control the actions of other
            users of the Website with whom you may choose to share your User
            Contributions. Therefore, we cannot and do not guarantee that User
            Contributions, if any, will not be viewed by unauthorized persons.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>COOKIES.</strong> The technologies we use for this automatic
            data collection of Technical Data may include Cookies (or browser
            cookies). Our{" "}
            <Link href="/cookies" target="_blank">
              Cookie Notice
            </Link>{" "}
            provides details on how we use cookies and your choices about
            cookies. A cookie is a small file placed on the hard drive of your
            computer. Cookies enable the site's or service provider's systems to
            recognize your browser and capture and remember certain information
            regarding your visit to the Website such as: your IP address; the
            domain from which you accessed the Website; the date, time and
            duration of your visit; and the pages you visited.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>
              THIRD-PARTY USE OF COOKIES AND OTHER TRACKING TECHNOLOGIES.
            </strong>
            Some content or applications, including advertisements, on our
            Services are served by third-parties, including advertisers, ad
            networks and servers, content providers and application providers.
            These third parties may use cookies or other tracking technologies
            to collect information about you when you use our Services. The
            information they collect may be associated with your personal
            information or they may collect information, including personal
            information, about your online activities over time and across
            different websites and other online services. They may use this
            information to provide you with interest-based (behavioral)
            advertising or other targeted content.
          </Typography>
          <Typography variant="body1" paragraph>
            We do not control these third parties' tracking technologies or how
            they may be used. If you have any questions about an advertisement
            or other targeted content, you should contact the responsible
            provider directly. When you leave our Website, we encourage you to
            read the privacy notice of every website you visit and every
            application you use.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            4. HOW WE USE YOUR PERSONAL DATA
          </Typography>
          <Typography variant="body1" paragraph>
            As permitted by law, we use information that we collect about you or
            that you provide to us, including your Personal Data:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                To carry out our obligations and enforce our rights arising from
                any contracts entered into between you and us, including without
                limitation to:
                <ul
                  style={{
                    display: "block",
                    marginBlockStart: "4px",
                    marginBlockEnd: "4px",
                    marginInlineStart: "0px",
                    marginInlineEnd: "0px",
                    paddingInlineStart: "40px",
                  }}
                >
                  <li>
                    provide our Services and content, and any information you
                    request from us, to you;
                  </li>
                  <li>
                    allow you to participate in interactive features of our
                    Services;
                  </li>
                  <li>
                    for billing and collection purposes, provide notices to you
                    about your account and/or subscription, including expiration
                    and renewal notices;
                  </li>
                  <li>
                    create, maintain, customize and secure your account with us;
                  </li>
                  <li>
                    personalize your Website experience and to deliver content
                    and products and service offerings relevant to your
                    interests;
                  </li>
                  <li>
                    provide notices to you about changes to our Services or any
                    products or services we offer;
                  </li>
                  <li>
                    to provide notices to you of updates to the Privacy Policy
                    or other terms of service;
                  </li>
                  <li>
                    and evaluate or conduct a merger, divestiture,
                    restructuring, reorganization, dissolution, or other sale or
                    transfer of some or all of our assets, whether as a going
                    concern or as part of bankruptcy, liquidation, or similar
                    proceeding, in which personal information held by us about
                    our consumers is among the assets transferred.
                  </li>
                </ul>
              </li>
              <li>
                Where it is necessary for our legitimate interests (or those of
                a third party) and your interests and fundamental rights do not
                override those interests.
              </li>
              <li>
                Where we need to respond to law enforcement requests (such as a
                search warrant or subpoena) and court orders, and to comply with
                legal or regulatory obligations.
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            We generally do not rely on consent as the legal basis for
            processing your Personal Data other than in relation to sending
            third party or direct marketing communications to you via email
            about our own and third-parties' goods and services that may be of
            interest to you. You have the right to withdraw consent to such
            marketing by emailing the address provided in the Contact
            Information section below.
          </Typography>
          <Typography variant="body1" paragraph>
            We may use the information we have collected from you to enable us
            to display advertisements to our advertisers' target audiences. Even
            though we do not disclose your personal information for these
            purposes without your consent, if you click on or otherwise interact
            with an advertisement, the advertiser may assume that you meet its
            target criteria.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>PURPOSES FOR WHICH WE WILL USE YOUR PERSONAL DATA.</strong>
            We set out below, in a table format, a description of all the ways
            we plan to use your Personal Data, and which of the legal bases we
            rely on to do so. Note that we may process your Personal Data for
            more than one lawful ground depending on the specific purpose for
            which we are using your Personal Data. Please contact us if you need
            details about the specific legal ground we are relying on to process
            your Personal Data where more than one ground has been set out in
            the table below:
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table
              size="small"
              aria-label="PURPOSES FOR WHICH WE WILL USE YOUR PERSONAL DATA"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">Purpose/Activity</TableCell>
                  <TableCell align="center">Type of Data</TableCell>
                  <TableCell align="center">
                    Lawful basis for processing including the basis of
                    legitimate interest if applicable
                  </TableCell>
                  <TableCell align="center">Data Retention Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To register you as a new customer
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                    </ol>
                  </TableCell>
                  <TableCell>Performance of a contract with you.</TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To process and deliver your order including: (1) manage
                    payments, fees, and charges; and (2) collect and recover
                    money owed to us
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                      <li>Transaction</li>
                      <li>Marketing and Communications</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ol
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Performance of a contract with you</li>
                      <li>
                        Necessary for our legitimate interests (to recover debts
                        due to us).
                      </li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To manage our relationship with you which will include: (1)
                    notifying you about changes to our Privacy Policy or terms
                    of use; (2) asking you to leave a review or take a survey
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                      <li>Profile</li>
                      <li>Usage</li>
                      <li>Marketing and Communication</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ol
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Performance of a contract with you</li>
                      <li>Necessary to comply with a legal obligation</li>
                      <li>
                        Necessary for our legitimate interests (to keep our
                        records updated and to study how customers use our
                        Services).
                      </li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To administer and protect our business, Website, and
                    Services (including troubleshooting, data analysis, testing,
                    system maintenance, support, reporting, and hosting of data)
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ol
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Performance of a contract with you</li>
                      <li>
                        Necessary for our legitimate interests (to study how
                        customers use our Services, to develop them and grow our
                        business.)
                      </li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To deliver relevant content via the Services and
                    advertisements to you and measure or understand the
                    effectiveness of the advertising we serve to you.
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                      <li>Profile</li>
                      <li>Usage</li>
                      <li>Marketing and Communication</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    Necessary for our legitimate interests (to study how
                    customers use our Services, to develop them, to grow our
                    business and to inform our marketing strategy).
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To make suggestions and recommendations to you about goods
                    and services that may be of interest to you.
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Identity</li>
                      <li>Contact</li>
                      <li>Usage</li>
                      <li>Profile</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    Necessary for our legitimate interests (to develop our
                    Services and grow our business).
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    To use data analytics to improve the Services, products,
                    marketing and user experiences.
                  </TableCell>
                  <TableCell>
                    <ol
                      type="a"
                      style={{
                        display: "block",
                        marginBlockStart: "4px",
                        marginBlockEnd: "4px",
                        marginInlineStart: "0px",
                        marginInlineEnd: "0px",
                        paddingInlineStart: "16px",
                      }}
                    >
                      <li>Technical</li>
                      <li>Usage</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    Necessary for our legitimate interests (to define types of
                    users of our services, to keep the Services updated and
                    relevant, to develop our business and inform out marketing
                    strategy).
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body1" paragraph>
            "<strong>Legitimate Interest</strong>" means the interest of the
            Company's business in conducting and managing our business to give
            you the best service/products and the best and most secure
            experience. We make sure we consider and balance any potential
            impact on you, both positive and negative, and your rights before we
            process your Personal Data for our legitimate interests. We do not
            use your Personal Data for activities where our interests are
            overridden by the impact on you, unless we have your consent or are
            otherwise required or permitted to by law. You can obtain further
            information about how we assess our legitimate interests against any
            potential impact on you in respect of specific activities by
            contacting us at the address listed in the Contact Information
            section below.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>MARKETING.</strong> We strive to provide you with choices
            regarding certain uses of your Personal Data, particularly around
            marketing and advertising.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>PROMOTIONAL OFFERS FROM THE COMPANY.</strong> We may use
            your Identity, Contact, Usage, and Profile Data to form a view on
            what we think you may want or need, or what may be of interest to
            you. This is how we decide which products, services, and offers may
            be relevant for you. We call this marketing.
          </Typography>
          <Typography variant="body1" paragraph>
            You will receive marketing communications from us if you have
            requested information from us or purchased Services from us, and in
            each case, if you have not opted out of receiving that marketing.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>THIRD-PARTY MARKETING.</strong> We will get your express
            opt-in consent before we share your Personal Data with any company
            outside of Best Coast Pairings LLC for marketing purposes.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>OPTING OUT.</strong> You can ask us or third parties to stop
            sending you marketing messages at any time by contacting us at the
            email address provided in the Contact Information section below.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>CHANGE OF PURPOSE.</strong> We will only use your Personal
            Data for the purposes for which we collected it, unless we
            reasonably determine that we need to use it for another reason
            compatible with the original purpose. If you wish to get an
            explanation as to how the processing for the new purpose is
            compatible with the original purpose, please contact us at the email
            address provided in the Contact Information section below.
          </Typography>
          <Typography variant="body1" paragraph>
            If we need to use your Personal Data for an unrelated purpose, we
            will notify you and we will explain the legal basis which allows us
            to do so. Please note that we may process your Personal Data without
            your knowledge or consent, in compliance with the above rules, where
            such processing is required or permitted by law.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            5. DISCLOSURES OF YOUR PERSONAL DATA
          </Typography>
          <Typography variant="body1" paragraph>
            We may disclose aggregated information about our users, and
            information that does not identify any individual, without
            restriction.
          </Typography>
          <Typography variant="body1" paragraph>
            We may disclose Personal Data that we collect or you provide as
            described in this Policy to enforce our Website policies, or protect
            our or other's rights, property, or safety; to comply with any law,
            court order or legal process, including responding to any government
            or regulatory request; and with the parties described below for the
            purposes set forth in the table in Section 4 above:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                Internal Third Parties, such as our subsidiaries and affiliates.
              </li>
              <li>
                External Third Parties, such as:
                <ul
                  style={{
                    display: "block",
                    marginBlockStart: "4px",
                    marginBlockEnd: "4px",
                    marginInlineStart: "0px",
                    marginInlineEnd: "0px",
                    paddingInlineStart: "40px",
                  }}
                >
                  <li>
                    To contractors, service providers and other third parties we
                    use to support our business and who are bound by contractual
                    obligations to keep personal information confidential and
                    use it only for the purposes for which we disclose it to
                    them.
                  </li>
                  <li>
                    To a buyer or other successor in the event of a merger,
                    divestiture, restructuring, reorganization, dissolution or
                    other sale or transfer of some or all of Best Coast Pairings
                    LLC's assets, whether as a going concern or as part of
                    bankruptcy, liquidation or similar proceeding, in which
                    personal information held by Best Coast Pairings LLC about
                    our Services' users is among the assets transferred.
                  </li>
                  <li>
                    To third parties to market their products or services to you
                    if you have consented to or not opted out of these
                    disclosures.
                  </li>
                  <li>
                    To third parties (e.g., shops) for estimating attendance,
                    ticket verification purpose.
                  </li>
                </ul>
              </li>
            </ul>
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            6. USE OF WEBSITE BY CHILDREN (UNDER THE AGE OF 16 IN THE EUROPEAN
            UNION OR AGE 13 IN THE UNITED STATES) / CHILDREN'S PRIVACY POLICY
          </Typography>
          <Typography variant="body1" paragraph>
            Our Website and Services are not intended for children under the age
            of 16 in the EU or children located in the US who are under age 13,
            and we do not knowingly collect, use, disclose or sell Personal Data
            from such children. No one under age 16 located in the EU, or age 13
            in the US, may provide any Personal Data to or through our Services.
            If you are under age 16 located in the EU, or under age 13 in the
            US, do not use or provide any information on or through any of the
            Services' features, register for the Services, use any of the
            interactive features of the Services, or provide any information
            about yourself to us. If you know or suspect that your child has
            submitted Personal Information to us and you would like to request
            that such information, be removed, you may do so by contacting us at
            the address listed below in the Contact Information section.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            7. INTERNATIONAL TRANSFER
          </Typography>
          <Typography variant="body1" paragraph>
            If you are located in a province, country, or other government
            jurisdiction outside of the United States, and if you choose to
            provide your personal information to Best Coast Pairings LLC through
            our Services, please be aware that your personal information will be
            transferred to and maintained on computers located in the United
            States and the applicable US privacy laws may not be as protective
            as those in your jurisdiction.
          </Typography>
          <Typography variant="body1" paragraph>
            Whenever we transfer your personal data to countries outside of the
            European Economic Area ("<strong>EEA</strong>"), we ensure a similar
            degree of protection is afforded to it by ensuring at least one of
            the following safeguards is implemented:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                Where we use certain service providers, we may use specific
                contracts approved by the European Commission which give
                personal data the same protection it has in Europe.
              </li>
              <li>
                Where we use providers based in the US, we may transfer data to
                them if they are part of the Data Privacy Framework (DPF)
                Program which requires them to provide similar protection to
                personal data shared between the EU and the US.
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            Please contact us if you would like further information on the
            specific mechanism used by us when transferring your personal data
            out of the EEA.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            8. DATA SECURITY
          </Typography>
          <Typography variant="body1" paragraph>
            We have implemented measures designed to secure your personal
            information from accidental loss and from unauthorized access, use,
            alteration and disclosure. We also limit access to Personal Data to
            those employees, agents, contractors, and other third parties who
            have a business need to know. These individuals will only process
            your Personal Data on our instructions and they are subject to a
            duty of confidentiality.
          </Typography>
          <Typography variant="body1" paragraph>
            Unfortunately, the transmission of information via the internet is
            not completely secure. Although we do our best to protect your
            personal information, we cannot guarantee the security of your
            personal information transmitted to or through our Services. Any
            transmission of personal information is at your own risk. We are not
            responsible for circumvention of any privacy settings or security
            measures contained on the Services.
          </Typography>
          <Typography variant="body1" paragraph>
            All Financial Data is securely processed by Stripe, Inc. and
            Stripe's privacy policy is available here (
            <Link href="https://stripe.com/privacy" target="_blank">
              https://stripe.com/privacy
            </Link>
            ).
          </Typography>
          <Typography variant="body1" paragraph>
            We have put in place procedures to deal with any suspected incident
            involving Personal Data and will notify you and any applicable
            regulator of an incident involving Personal Data where we are
            legally required to do so.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            9. DATA RETENTION
          </Typography>
          <Typography variant="body1" paragraph>
            We maintain one or more databases to store your Personal Data and
            may keep such information indefinitely in order to fulfill the
            purposes we collected it for, including for the purposes of
            satisfying any legal, accounting, or reporting requirements. In some
            circumstances you may ask us to delete your Personal Data. See the
            section below regarding Requesting Erasure for more information. In
            some circumstances we may anonymize your Personal Data so that it
            can no longer be associated with you for research or statistical
            purposes in which case we may use this information indefinitely
            without further notice to you.
          </Typography>
          <Typography variant="body1" paragraph>
            Financial Data is collected and maintained by our third-party
            payment processor, Stripe, Inc., and is subject to Stripe, Inc.'s
            data retention policy. More information can be found here (
            <Link
              href="https://support.stripe.com/questions/data-retention-policy"
              target="_blank"
            >
              https://support.stripe.com/questions/data-retention-policy
            </Link>
            ).
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            10. YOUR CHOICES ABOUT HOW WE USE AND DISCLOSE YOUR INFORMATION.
          </Typography>
          <Typography variant="body1" paragraph>
            We strive to provide you with choices regarding the personal
            information you provide to us. We have created mechanisms to provide
            you with the following control over your information:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                <strong>Tracking Technologies and Advertising.</strong> You can
                set your browser to refuse all or some browser cookies, or to
                alert you when cookies are being sent. To learn how you can
                manage your Flash cookie settings, visit the Flash player
                settings page on Adobe's{" "}
                <Link
                  href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html"
                  target="_blank"
                >
                  website
                </Link>
                . If you disable or refuse cookies, please note that some parts
                of this site may then be inaccessible or not function properly.
              </li>
              <li>
                <strong>
                  Disclosure of Your Information for Third-Party Advertising.
                </strong>{" "}
                If you do not want us to share your personal information with
                unaffiliated or non-agent third parties for promotional
                purposes, you can opt-out by sending an email to the email
                address provided in the Contact Information section below.
              </li>
              <li>
                <strong>Promotional Offers from the Company.</strong> If you do
                not wish to have your e-mail address used by the Company to
                promote our own or third parties' products or services, you can
                opt-out by sending us an e-mail stating your request to the
                e-mail address provided below in the Contact Information
                section. If we have sent you a promotional e-mail, you may send
                us a return e-mail asking to be omitted from future e-mail
                distributions. This opt out does not apply to communications
                with the Company as a result of a product or subscription
                purchase, product service experience or other transactions.
              </li>
              <li>
                <strong>Targeted Advertising.</strong> If you do not want us to
                use information that we collect or that you provide to us to
                deliver advertisements according to our advertisers'
                target-audience preferences, you can opt-out by sending us an
                e-mail stating your request to the e-mail address provided below
                in the Contact Information section.
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            We do not control third parties' collection or use of your
            information to serve interest-based advertising. However, these
            third parties may provide you with ways to choose not to have your
            information collected or used in this way. You can opt out of
            receiving targeted ads from members of the Network Advertising
            Initiative ("<strong>NAI</strong>") on the{" "}
            <Link href="https://thenai.org/opt-out/" target="_blank">
              NAI's website
            </Link>
            .
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            11. YOUR LEGAL RIGHTS FOR CONSUMERS OUTSIDE OF THE UNITED STATES.
          </Typography>
          <Typography variant="body1" paragraph>
            Under certain circumstances, consumers in the EEA and other
            jurisdictions that have adopted privacy laws or regulations similar
            to GDPR have rights regarding their Personal Data. Each of the
            rights is summarized below:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                <strong>Request access to your Personal Data.</strong> You have
                the right to be informed of the Personal Data we hold about you,
                and why and how we have it. You may also request certain
                information regarding Company's disclosure of Personal Data to
                third parties for their direct marketing purposes.
              </li>
              <li>
                <strong>Request correction of your Personal Data.</strong> You
                have the right to request correction of inaccurate or incomplete
                Personal Data although we will need to verify the accuracy of
                the new data you provide to us. We may require you to take
                certain steps or provide additional information to verify your
                identity before we provide any information or make any
                corrections. We may not be able to accommodate a request to
                change information if we believe the change would violate any
                law or legal requirement or cause the information to be
                incorrect.
              </li>
              <li>
                <strong>Request erasure of your Personal Data.</strong> You have
                the right to have your Personal Data erased if there is no good
                reason for us to continue to process it. You also have the right
                to ask us to delete/erase your Personal Data where you have
                successfully exercised your right to object to its processing
                (see below), where we may have processed your information
                unlawfully or where we are required to erase your Personal Data
                to comply with local law. Note, however, that we may not always
                be able to comply with your request to erase for specific legal
                reasons which will be provided to you, if applicable, when
                evaluating your request. By requesting that we delete your
                Personal Data, we may keep the anonymized or de-identified
                statistical data (including, without limitation, your tournament
                score information), that is not connected to your name or other
                Personal Data or identity.
              </li>
              <li>
                <strong>Object to processing of your Personal Data.</strong> You
                have the right to object to the processing of your Personal Data
                where we have justified the data processing on the basis of our
                legitimate interests and there is something about your
                particular situation which makes you feel the processing impacts
                your fundamental rights and freedoms. You also have the right to
                object where we are processing your Personal Data for direct
                marketing purposes.
              </li>
              <li>
                <strong>
                  Request restriction of processing your Personal Data.
                </strong>{" "}
                You have the right to restrict the processing of your Personal
                Data if: the accuracy of the data is being contested for as long
                as it takes to verify that accuracy; the processing is unlawful
                and you request restriction of processing (rather than erasure
                of the data); we no longer need the data for its original
                purpose but you would like us to hold the data because you need
                it to establish, exercise, or defend legal rights; or you have
                objected to our use of your Personal Data but we need to verify
                whether we have overriding legitimate interests to use it.
              </li>
              <li>
                <strong>Request transfer of your Personal Data.</strong> You
                have the right to request the transfer of your Personal Data to
                you or to a third party. We will provide to you, or a third
                party you designate, your Personal Data in a structured,
                commonly used, machine-readable format. This right only applies
                to automated information which you initially provided consent
                for us to use or where we used the information to perform a
                contract with you.
              </li>
              <li>
                <strong>Right to withdraw consent.</strong> You have the right
                to withdraw your consent to the processing of your Personal Data
                where we were are relying on consent to process your Personal
                Data. However, this will not affect the lawfulness of any
                processing carried out before you withdraw your consent. If you
                withdraw your consent, we may not be able to provide certain
                services to you. We will advise you if this is the case when
                reviewing your request to withdraw your consent.
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            You may exercise any of these rights by e-mailing us at the address
            listed in the Contact Information section below. You will not have
            to pay a fee to access your Personal Data or to exercise any of the
            other rights. However, we may charge a reasonable fee if your
            request is clearly unfounded, repetitive, or excessive.
            Alternatively, we may refuse to comply with your request in such
            circumstances. Also, the exercise of some rights will prevent our
            ability to provide Services to you, and if this is the case, we will
            explain this to when you submit your request to us.
          </Typography>
          <Typography variant="body1" paragraph>
            We may need to request specific information from you to help us
            confirm your identity and ensure your right to access your Personal
            Data or to exercise any of your other rights. This is a security
            measure to ensure that Personal Data is not disclosed to any person
            who has no right to receive it. We may also contact you to ask you
            for further information in relation to your request to speed up our
            response.
          </Typography>
          <Typography variant="body1" paragraph>
            We try to respond to all legitimate requests within one month after
            the request is received. Occasionally it may take us longer than a
            month if your request is particularly complex or you have submitted
            several requests. In this case, we will notify you and keep you
            updated.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            12. YOUR LEGAL RIGHTS FOR CONSUMERS IN THE UNITED STATES
          </Typography>
          <Typography variant="body1" paragraph>
            In addition to the provisions of our Privacy Policy set forth above,
            the Company recognizes that other jurisdictions have passed their
            own comprehensive privacy laws, including several states in the US.
            Unless the provisions below states that it specifically applies to
            residents of a particular state, the following provisions apply to
            all US-based consumers.
          </Typography>
          <Typography variant="body1" paragraph>
            Under California law, California residents have specific rights
            regarding their Personal Information and must be provided a
            particular notice when Personal Information is collected (the
            "Notice at Collection"). Our Notice of Collection is comprised of
            the following links:
          </Typography>
          <Typography variant="body1" paragraph>
            <ul
              style={{
                display: "block",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                The section of this Privacy Policy, below, entitled "Personal
                Information Defined."
              </li>
              <li>
                The section of this Privacy Policy entitled "How We Use Your
                Personal Data" and "Disclosures Regarding Collection,
                Disclosure, Sale and Sharing of Personal Information."
              </li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            Personal Information Defined. "Personal Data" as used above includes
            the "Personal Information" described below with respect to US-based
            consumers. The Company has collected the following categories of
            Personal Information from consumers within the last 12 months:
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table
              size="small"
              aria-label="YOUR LEGAL RIGHTS FOR CONSUMERS IN THE UNITED STATES"
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Examples of Information Under This Category{" "}
                    <i>
                      (*Not all of this information is collected by the Company)
                    </i>
                  </TableCell>
                  <TableCell align="center">
                    Whether this Information is Collected
                  </TableCell>
                  <TableCell align="center">Retention Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>A. Identifiers</strong>
                    </Typography>
                    A real name, alias, postal address, unique personal
                    identifier, online identifier, Internet Protocol address,
                    email address, account name, Social Security number,
                    driver's license number, government issued identification
                    number, or other similar identifiers.
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        B. Personal information categories listed in the
                        California Customer Records statute (Cal. Civ. Code
                        section 1798.80(e))
                      </strong>
                    </Typography>
                    A name, signature, Social Security number, physical
                    characteristics or description, address, telephone number,
                    government issued identification number, driver's license or
                    state identification card number, insurance policy number,
                    education, employment, employment history, bank account
                    number, credit card number, debit card number, or any other
                    financial information, medical information, or health
                    insurance information.
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        C. Protected classification characteristics under
                        California or federal law
                      </strong>
                    </Typography>
                    Age (40 years or older), race, color, ancestry, national
                    origin, citizenship, religion or creed, marital status,
                    medical condition, physical or mental disability, sex
                    (including gender, gender identity, gender expression,
                    pregnancy or childbirth and related medical conditions),
                    sexual orientation, veteran or military status, genetic
                    information (including familial genetic information).
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>D. Commercial information</strong>
                    </Typography>
                    Records of personal property, products or services
                    purchased, obtained, or considered, or other purchasing or
                    consuming histories or tendencies.
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category. OR
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>E. Biometric information</strong>
                    </Typography>
                    Genetic, physiological, behavioral, and biological
                    characteristics, or activity patterns used to extract a
                    template or other identifier or identifying information such
                    as, fingerprints, faceprints and voiceprints, iris or retina
                    scans, keystroke, gait, or other physical patterns, and
                    sleep, health, or exercise data.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        F. Internet or other similar network activity
                      </strong>
                    </Typography>
                    Browsing history, search history, information on a
                    consumer's interaction with a website, application, or
                    advertisement.
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>G. Geolocation data</strong>
                    </Typography>
                    Physical location or movements, including physical location
                    and time related to use of an internet website, application,
                    or device.
                  </TableCell>
                  <TableCell>
                    We may collect and use Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    For as long as the Company has a business need or is legally
                    required to maintain/use such information.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>H. Sensory data</strong>
                    </Typography>
                    Audio, electronic, visual, thermal, olfactory, or similar
                    information and call monitoring.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        I. Professional or employment-related information
                      </strong>
                    </Typography>
                    Current or past job history or performance evaluations.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        J. Non-public education information (per the Family
                        Educational Rights and Privacy Act (20 U.S.W. Section
                        1232g, 34 C.F.R. Part 99))
                      </strong>
                    </Typography>
                    Education records directly related to a student maintained
                    by an educational institution or party acting on its behalf,
                    such as grades, transcripts, class lists, student schedules,
                    student identification codes, student financial information,
                    or student disciplinary records.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        K. Inferences drawn from other personal information.
                      </strong>
                    </Typography>
                    Profile reflecting a person's preferences, characteristics,
                    psychological trends, predispositions, behavior, attitudes,
                    intelligence, abilities, and aptitudes.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">
                      <strong>
                        L. Sensitive Personal Information means certain types of
                        Personal Information, such as:
                      </strong>
                    </Typography>
                    Social security, driver's license, state identification
                    card, or passport number, account log-in, financial account,
                    debit card, or credit card number in combination with any
                    required security or access code, password, or credentials
                    allowing access to an account, precise geolocation,
                    including any data that is derived from a device and that is
                    used or intended to be used to locate a consumer within a
                    geographic area that is equal to or less than the area of a
                    circle with a radius of 1,850 feet, except as prescribed by
                    regulations, racial or ethnic origin, citizenship or
                    immigration information, religious or philosophical beliefs,
                    or union membership, the contents of a consumer's mail,
                    email, and text messages unless the business is the intended
                    recipient of the communication, genetic data, unique
                    biometric information used for identification, consumer's
                    neural data, and health information.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                  <TableCell>
                    We do not collect any Personal Information within this
                    category.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body1" paragraph>
            <strong>
              DISCLOSURES REGARDING COLLECTION, DISCLOSURE, SALE AND SHARING OF
              PERSONAL INFORMATION.
            </strong>
          </Typography>
          <Typography variant="body1" paragraph>
            In the preceding twelve (12) month period, the Company has not sold
            any Personal Information of consumers, or shared any Personal
            Information for cross-context behavioral advertising, to any third
            parties.
          </Typography>
          <Typography variant="body1" paragraph>
            In the preceding twelve (12) months, Company has disclosed personal
            information for a business purpose to the categories of third
            parties indicated in{" "}
            <strong>
              Section 5 (Disclosures of Your Personal Data) of our Privacy
              Policy above.
            </strong>
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>CONSUMER REQUESTS.</strong> This section describes the
            rights of California residents under California law and how to
            exercise those rights by submitting a consumer request ("
            <strong>Consumer Request</strong>"). In some instances, where we are
            acting as a "service provider," we may direct consumer requests to
            the business who holds your Personal Information. Please note, we
            will seek to verify your identity when we receive an individual
            rights request from you in order to ensure the security of your
            Personal Information.
          </Typography>
          <Typography variant="body1" paragraph>
            <ol
              style={{
                display: "block",
                listStyleType: "decimal",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>
                <strong>
                  Access to Specific Information and Data Portability Rights.
                </strong>{" "}
                You have the right to request that the Company disclose certain
                or all of the following information to you about our collection
                and use of your Personal Information over the past 12 months.
                Once we receive a verifiable Consumer Request, we will be able
                to disclose to you the specific pieces of Personal Information
                we collected about you (also may be called a data portability
                request).
              </li>
              <li>
                <strong>Deletion Request Rights.</strong> You have the right to
                request that we delete any or all of your Personal Information
                that we collected from and about you, subject to certain
                statutory exceptions. Once we receive a verifiable Consumer
                Request, we will identify the Personal Information, confirm that
                you want that Personal Information deleted, and then delete (and
                notify our service providers to delete) your Personal
                Information from our records, unless an exception applies.
              </li>
              <li>
                <strong>Right to Correct.</strong> You have the right to request
                inaccurate Personal Information be corrected.
              </li>
              <li>
                <strong>
                  Right to Restrict Sensitive Personal Information Processing.
                </strong>{" "}
                You have the right to restrict how your Sensitive Personal
                Information is used and disclosed, subject to certain uses
                permitted by California law (such as, the use necessary for the
                performance of services, use to ensure security and integrity).
              </li>
              <li>
                <strong>Non-Discrimination.</strong> We will not discriminate
                against you for exercising any of your legal rights regarding
                your Personal Information, unless permitted by law. We will not
                take any of the following actions solely because you chose to
                exercise any of your legal rights regarding your Personal
                Information:
                <ol
                  type="a"
                  style={{
                    display: "block",
                    marginBlockStart: "4px",
                    marginBlockEnd: "4px",
                    marginInlineStart: "0px",
                    marginInlineEnd: "0px",
                    paddingInlineStart: "40px",
                  }}
                >
                  <li>Deny you goods or services.</li>
                  <li>
                    Charge you different prices or rates for goods or services,
                    including through granting discounts or other benefits or
                    imposing penalties.
                  </li>
                  <li>
                    Provide you with a different level or quality of goods or
                    services.
                  </li>
                  <li>
                    Suggest that you may receive a different price or rate for
                    goods or services or a different level or quality of goods
                    or services.
                  </li>
                </ol>
              </li>
            </ol>
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>HOW TO SUBMIT A CONSUMER REQUEST.</strong> To exercise your
            rights, please submit a Consumer Request to the contact information
            below. The Consumer Request must describe the request with
            sufficient detail that allows us to properly understand, evaluate
            and respond to it. Please specify if your Consumer Request pertains
            to all specific pieces of your Personal Information, or only with
            respect to the categories of Personal Information. You may only make
            a Consumer Request for access or data portability twice within a
            12-month period.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>HOW WE WILL VERIFY CONSUMER REQUESTS.</strong> Only you, or
            a person or entity that you authorize to act on your behalf, may
            make a Consumer Request related to your Personal Information. To
            designate an authorized agent, we will ask you to provide us with
            legal documentation (such as power of attorney documentation) or
            written permission and the agent will need to verify their own
            identity with us.
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to require you to provide additional
            information to enable us to reasonably verify that you are the
            person about whom the Personal Information pertains in order to
            release the requested information to you (or your authorized
            representative). If you have an account with us, you will be asked
            to use the standard authentication procedures to access your
            account. If you do not have an account with us, you will be asked to
            verify certain pieces of your Personal Information. If we are unable
            to verify your identity, we may be unable to provide you with
            information responsive to the request.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>HOW WE WILL RESPOND TO CONSUMER REQUESTS.</strong> We
            endeavor to respond to verifiable Consumer Requests within 45 days
            after receipt. If we require more time (up to 45 days) we will
            inform you of the reason and extension in writing. If you have an
            account with us, we will deliver our written response(s) to that
            account. If you do not have an account with us, we will deliver our
            written response(s) by mail or electronically, at your option. Any
            disclosures we provide will only cover the 12-month period preceding
            receipt of the Consumer Request. If we deny (or cannot comply with)
            the Consumer Request, we will explain why. For data portability
            requests, we will select a format to provide your Personal
            Information that is readily useable and should allow you to transmit
            the information from one entity to another entity. We do not charge
            a fee to process or respond to your Consumer Request unless such
            request is excessive, repetitive, or manifestly unfounded. If we
            determine that the request warrants a fee, we will tell you why and
            provide you with a cost estimate before completing your request.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>ADDITIONAL RIGHTS UNDER CALIFORNIA LAW.</strong> California
            Civil Code Section § 1798.83 permits users of our Services that are
            California residents to request certain information regarding our
            disclosure of personal information to third parties for their direct
            marketing purposes. To make such a request, please e-mail us at the
            address listed in the Contact Information section below.
          </Typography>
          <Typography variant="body1" paragraph>
            If you are a California resident under the age of 18, you may
            request removal of content or information you have posted publicly
            on our Website. To request deletion or de-identification of a post
            publicly on our Website, please contact us directly at the address
            below in the Contact Information section. Please understand that
            there may be de-identified or recoverable elements of your content
            or information on our servers in some form, and will not remove
            content or information that we may be required to retain under
            applicable federal or state laws.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>CONSUMER RIGHTS UNDER OTHER STATE PRIVACY LAWS.</strong>{" "}
            Additional states (such as Colorado, Connecticut, Delaware, Florida,
            Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New
            Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah,
            and Virginia) provide (now or in the future) their state residents
            with rights to:
          </Typography>
          <Typography variant="body1" paragraph>
            <ol
              style={{
                display: "block",
                listStyleType: "decimal",
                marginBlockStart: "4px",
                marginBlockEnd: "4px",
                marginInlineStart: "0px",
                marginInlineEnd: "0px",
                paddingInlineStart: "40px",
              }}
            >
              <li>Confirm whether we process their Personal Data.</li>
              <li>Access and delete certain Personal Data.</li>
              <li>
                Correct inaccuracies in their Personal Data, taking into account
                the information's nature processing purpose.
              </li>
              <li>Data portability.</li>
              <li>
                Opt-out of Personal Data processing for:
                <ol
                  type="a"
                  style={{
                    display: "block",
                    marginBlockStart: "4px",
                    marginBlockEnd: "4px",
                    marginInlineStart: "0px",
                    marginInlineEnd: "0px",
                    paddingInlineStart: "40px",
                  }}
                >
                  <li>Targeted advertising;</li>
                  <li>Sales; or</li>
                  <li>
                    Profiling in furtherance of decisions that produce legal or
                    similarly significant effects.
                  </li>
                </ol>
              </li>
              <li>
                Either limit (opt-out of) or require consent to process
                sensitive Personal Data.
              </li>
            </ol>
          </Typography>
          <Typography variant="body1" paragraph>
            The exact scope of these rights may vary by state. To make such a
            request, please email us at the address listed in the "
            <i>Contact Information</i>" section below. In the event that we deny
            a resident's data request, the resident may appeal said decision by
            emailing the address listed below in the "<i>Contacting Us</i>"
            section. If a Colorado resident has concerns about the result of the
            appeal, they have the right to contact the Attorney General for the
            state of Colorado.
          </Typography>
        </>

        <>
          <Typography variant="h4" paragraph>
            13. CONTACT INFORMATION
          </Typography>
          <Typography variant="body1" paragraph>
            To ask questions or comment about this Policy and our privacy
            practices, please email us at:{" "}
            <Link href="mailto:support@bestcoastpairings.com" target="_blank">
              support@bestcoastpairings.com
            </Link>
            .
          </Typography>
        </>
      </Container>
    </Box>
  );
}
